import React, { useEffect, useState } from "react";
import DetailsPopup from "../components/DetailsPopup";
import { EmptyField, FieldShell } from "../components/FieldShell";
import {
  buildDetailSource,
  extractApiArray,
  getArrayChipLabel,
  getArrayItems,
} from "../utils/arrayUtils";
import { isTrue, objectLabel } from "../utils/valueUtils";

const arrayApiCache = new Map();

const looksLikeMongoId = (value) =>
  /^[a-f\d]{24}$/i.test(String(value || "").trim());

export default function ArrayField({ row, label, column, raw }) {
  const items = getArrayItems(row, column, raw);
  const showDetails = isTrue(column.details);
  const detailFields = Array.isArray(column.BoxShow) ? column.BoxShow : [];

  const [apiRows, setApiRows] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  const needsLabelResolution = items.some((item) => {
    if (item === null || item === undefined || item === "") return false;
    if (typeof item !== "object") return looksLikeMongoId(item);

    const labelValue = getArrayChipLabel(item, column);
    return !labelValue || labelValue === "—" || looksLikeMongoId(labelValue);
  });

  useEffect(() => {
    const shouldFetch =
      Boolean(column.api) &&
      items.length > 0 &&
      ((showDetails && detailFields.length > 0) || needsLabelResolution);

    if (!shouldFetch) {
      return undefined;
    }

    const controller = new AbortController();

    const fetchDetailsData = async () => {
      try {
        setApiLoading(true);
        setApiError("");

        if (arrayApiCache.has(column.api)) {
          setApiRows(arrayApiCache.get(column.api));
          return;
        }

        const token =
          localStorage.getItem("token") ||
          localStorage.getItem("accessToken") ||
          localStorage.getItem("authToken");

        const response = await fetch(column.api, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${label} details`);
        }

        const payload = await response.json();
        const rows = extractApiArray(payload);
        arrayApiCache.set(column.api, rows);
        setApiRows(rows);
      } catch (error) {
        if (error.name !== "AbortError") {
          setApiError(error.message || "Unable to load details");
        }
      } finally {
        if (!controller.signal.aborted) setApiLoading(false);
      }
    };

    fetchDetailsData();
    return () => controller.abort();
  }, [
    showDetails,
    column.api,
    column.field,
    column.apiTitle,
    detailFields.length,
    items.length,
    needsLabelResolution,
    label,
  ]);

  useEffect(() => {
    setSelectedItem(null);
  }, [row, column.field]);

  if (!items.length) {
    return <EmptyField label={label} column={column} />;
  }

  const selectedSource = selectedItem
    ? buildDetailSource(selectedItem, apiRows, column)
    : null;

  return (
    <>
      <FieldShell label={label} column={column}>
        <div className="safety-row-details__chipset">
          {items.map((item, itemIndex) => {
            const detailSource = buildDetailSource(item, apiRows, column);
            const resolvedLabel =
              getArrayChipLabel(detailSource || item, column) ||
              objectLabel(detailSource, column) ||
              "—";

            const canShowDetails = showDetails && detailFields.length > 0;

            return (
              <div
                key={
                  item?._id ||
                  item?.id ||
                  item?.userId ||
                  `${resolvedLabel}-${itemIndex}`
                }
                className={`safety-row-details__chip-group ${
                  canShowDetails
                    ? "safety-row-details__chip-group--details"
                    : ""
                }`}
              >
                {canShowDetails ? (
                  <button
                    type="button"
                    className="safety-row-details__chip safety-row-details__chip--button"
                    onClick={() => setSelectedItem(item)}
                    title={`View ${resolvedLabel} details`}
                  >
                    {resolvedLabel}
                  </button>
                ) : (
                  <span className="safety-row-details__chip">
                    {resolvedLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </FieldShell>

      {selectedItem ? (
        <DetailsPopup
          title={`${label} Details`}
          source={selectedSource}
          fields={detailFields}
          loading={apiLoading}
          error={apiError}
          fallbackText="Details not found for this selected user."
          onClose={() => setSelectedItem(null)}
        />
      ) : null}
    </>
  );
}
